import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import AuthContext from '../context/AuthContext';
import { DeleteIcon, PlusIcon, EmailIcon, RefreshIcon } from './Icons';

const CloudflareManager = () => {
    const { user, API_URL } = useContext(AuthContext);
    const [routings, setRoutings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [newRule, setNewRule] = useState({
        name: '',
        matcherValue: '',
        actionValue: ''
    });
    const [showSystemRules, setShowSystemRules] = useState(false);
    const SYSTEM_EMAIL = 'pfadfinder.wutoeschingen@gmail.com';

    const fetchRoutings = async () => {
        if (!user || !user.token) return;
        try {
            setLoading(true);
            const { data } = await axios.get(`${API_URL}/cloudflare/routings`, {
                headers: { Authorization: `Bearer ${user.token}` }
            });
            setRoutings(Array.isArray(data) ? data : []);
            setError(null);
        } catch (err) {
            const msg = err.response?.data?.message || 'Cloudflare Daten konnten nicht geladen werden.';
            setError(msg);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchRoutings();
    }, [user, API_URL]);

    const handleCreate = async (e) => {
        e.preventDefault();
        try {
            const payload = {
                name: newRule.name,
                enabled: true,
                matchers: [{ type: 'literal', field: 'to', value: newRule.matcherValue }],
                actions: [{ type: 'forward', value: [newRule.actionValue] }]
            };
            await axios.post(`${API_URL}/cloudflare/routings`, payload, {
                headers: { Authorization: `Bearer ${user.token}` }
            });
            setNewRule({ name: '', matcherValue: '', actionValue: '' });
            fetchRoutings();
        } catch (err) {
            alert('Fehler beim Erstellen der Weiterleitung.');
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Diese Weiterleitung wirklich löschen?')) return;
        try {
            await axios.delete(`${API_URL}/cloudflare/routings/${id}`, {
                headers: { Authorization: `Bearer ${user.token}` }
            });
            fetchRoutings();
        } catch (err) {
            alert('Fehler beim Löschen.');
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center">
                        <EmailIcon style={{ fontSize: 20 }} />
                    </div>
                    <h3 className="text-title-large font-bold text-surface-on">Email Weiterleitungen (Cloudflare)</h3>
                </div>
                <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2 text-label-medium text-surface-onVariant cursor-pointer">
                        <input
                            type="checkbox"
                            checked={showSystemRules}
                            onChange={(e) => setShowSystemRules(e.target.checked)}
                            className="w-4 h-4 rounded border-outline"
                        />
                        System-Regeln anzeigen
                    </label>
                    <button onClick={fetchRoutings} className="md3-icon-btn hover:bg-surface-containerHighest">
                        <RefreshIcon style={{ fontSize: 20 }} />
                    </button>
                </div>
            </div>

            {/* Create Form */}
            <form onSubmit={handleCreate} className="bg-surface-containerLow p-6 rounded-3xl border border-outlineVariant space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <input
                        type="text"
                        placeholder="Name (z.B. Info Adresse)"
                        value={newRule.name}
                        onChange={(e) => setNewRule({ ...newRule, name: e.target.value })}
                        className="md3-textfield-outlined h-12"
                        required
                    />
                    <input
                        type="email"
                        placeholder="Von (z.B. info@deine-domain.de)"
                        value={newRule.matcherValue}
                        onChange={(e) => setNewRule({ ...newRule, matcherValue: e.target.value })}
                        className="md3-textfield-outlined h-12"
                        required
                    />
                    <input
                        type="email"
                        placeholder="An (z.B. privat@gmail.com)"
                        value={newRule.actionValue}
                        onChange={(e) => setNewRule({ ...newRule, actionValue: e.target.value })}
                        className="md3-textfield-outlined h-12"
                        required
                    />
                </div>
                <button type="submit" className="md3-btn-filled w-full md:w-auto px-8">
                    <PlusIcon className="mr-2" /> Erstellen
                </button>
            </form>

            {/* List */}
            {loading ? (
                <div className="text-center py-10 text-surface-onVariant">Lädt...</div>
            ) : error ? (
                <div className="p-6 bg-error-container text-error-on-container rounded-2xl">{error}</div>
            ) : (
                <div className="overflow-hidden rounded-2xl border border-outlineVariant">
                    <table className="w-full text-left border-collapse bg-surface-containerHigh">
                        <thead className="bg-surface-containerHighest">
                            <tr>
                                <th className="p-4 font-bold text-surface-onVariant">Name</th>
                                <th className="p-4 font-bold text-surface-onVariant">Von</th>
                                <th className="p-4 font-bold text-surface-onVariant">An</th>
                                <th className="p-4 font-bold text-surface-onVariant text-right">Aktionen</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-outlineVariant">
                            {routings
                                .filter(rule => {
                                    if (showSystemRules) return true;
                                    const isSystem = rule.actions?.[0]?.value?.[0] === SYSTEM_EMAIL;
                                    return !isSystem;
                                })
                                .map(rule => (
                                <tr key={rule.tag} className="hover:bg-surface-on/[0.04]">
                                    <td className="p-4 font-medium">{rule.name || (rule.matchers?.[0]?.type === 'all' ? 'Catch-all' : 'Unbenannt')}</td>
                                    <td className="p-4 text-primary">
                                        {rule.matchers?.[0]?.type === 'all' ? '*' : (rule.matchers?.[0]?.value || 'N/A')}
                                    </td>
                                    <td className="p-4 text-surface-onVariant">{rule.actions?.[0]?.value?.[0] || 'N/A'}</td>
                                    <td className="p-4 text-right">
                                        <button onClick={() => handleDelete(rule.tag)} className="md3-icon-btn text-error hover:bg-error-container">
                                            <DeleteIcon style={{ fontSize: 20 }} />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default CloudflareManager;
